import {
  addDays,
  addMinutes,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isEqual,
  startOfDay,
  startOfMinute,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMinutes,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import type { DateRange } from './types';
import { getRange } from './utils';

interface Stepper {
  isActive: (to: Date, from: Date) => boolean;
  previous: (from: Date, to: Date) => { from: Date; to: Date };
  next: (from: Date, to: Date) => { from: Date; to: Date };
}

export const steppers: Record<string, Stepper> = {
  year: {
    isActive: (from: Date, to: Date) =>
      isEqual(startOfYear(from), from) &&
      isEqual(startOfMinute(endOfYear(to)), startOfMinute(to)),
    previous: (from: Date, to: Date) => {
      const amount = differenceInYears(to, from) + 1;

      return {
        from: startOfYear(subYears(from, amount)),
        to: startOfMinute(endOfYear(subYears(from, 1))),
      };
    },
    next: (from: Date, to: Date) => {
      const amount = differenceInYears(to, from) + 1;

      return {
        from: startOfYear(addYears(to, 1)),
        to: startOfMinute(endOfYear(addYears(to, amount))),
      };
    },
  },
  month: {
    isActive: (from: Date, to: Date) =>
      isEqual(startOfMonth(from), from) &&
      isEqual(startOfMinute(endOfMonth(to)), startOfMinute(to)),
    previous: (from: Date, to: Date) => {
      const amount = differenceInMonths(to, from) + 1;

      return {
        from: startOfMonth(subMonths(from, amount)),
        to: startOfMinute(endOfMonth(subMonths(from, 1))),
      };
    },
    next: (from: Date, to: Date) => {
      const amount = differenceInMonths(to, from) + 1;

      return {
        from: startOfMonth(addMonths(to, 1)),
        to: startOfMinute(endOfMonth(addMonths(to, amount))),
      };
    },
  },
  week: {
    isActive: (from: Date, to: Date) =>
      isEqual(startOfWeek(from, { weekStartsOn: 1 }), from) &&
      isEqual(
        startOfMinute(endOfWeek(to, { weekStartsOn: 1 })),
        startOfMinute(to)
      ),
    previous: (from: Date, to: Date) => {
      const amount = differenceInWeeks(to, from) + 1;

      return {
        from: startOfWeek(subWeeks(from, amount), { weekStartsOn: 1 }),
        to: startOfMinute(endOfWeek(subWeeks(from, 1), { weekStartsOn: 1 })),
      };
    },
    next: (from: Date, to: Date) => {
      const amount = differenceInMonths(to, from) + 1;

      return {
        from: startOfWeek(addWeeks(to, 1), { weekStartsOn: 1 }),
        to: startOfMinute(endOfWeek(addWeeks(to, amount), { weekStartsOn: 1 })),
      };
    },
  },
  day: {
    isActive: (from: Date, to: Date) =>
      isEqual(startOfDay(from), from) &&
      isEqual(startOfMinute(endOfDay(to)), startOfMinute(to)),
    previous: (from: Date, to: Date) => {
      const amount = differenceInDays(to, from) + 1;

      return {
        from: startOfDay(subDays(from, amount)),
        to: startOfMinute(endOfDay(subDays(from, 1))),
      };
    },
    next: (from: Date, to: Date) => {
      const amount = differenceInDays(to, from) + 1;

      return {
        from: startOfDay(addDays(to, 1)),
        to: startOfMinute(endOfDay(addDays(to, amount))),
      };
    },
  },
  time: {
    isActive: () => true,
    previous: (from: Date, to: Date) => {
      const amount = differenceInMinutes(to, from);

      return {
        from: startOfMinute(subMinutes(from, amount)),
        to: startOfMinute(from),
      };
    },
    next: (from: Date, to: Date) => {
      const amount = differenceInMinutes(to, from);

      return {
        from: startOfMinute(to),
        to: startOfMinute(addMinutes(to, amount)),
      };
    },
  },
};

export function getStepper(value: string[] | DateRange) {
  const range = getRange(value);
  if (!range) {
    throw new Error('Illegal date range');
  }
  const { from, to } = range;
  const stepper =
    Object.values(steppers).find((stepper) => stepper.isActive(from, to)) ||
    steppers.time;

  return {
    previous: () => stepper.previous(from, to),
    next: () => stepper.next(from, to),
  };
}
